import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject, EMPTY, Observable, Subscription, of, switchMap } from 'rxjs';
import { tap, map, take, filter, mergeMap, finalize } from 'rxjs/operators';
import { findIndex } from 'lodash';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { EngagementEventTypeEnum } from '../../../../api/mentions/enum/engagement-event-type.enum';
import { EngagementAgeEnum } from '../../../../api/mentions/enum/engagement-age.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { Mention } from '../../models/mention';
import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { FileUploadModel } from '../../../Forms/models/file-upload.model';
import { isoLanguageNames } from '../../../Forms/data/iso-language-names';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { MediaTypeModel } from '../../../../Monitor/Shared/models/media-type.model';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { JournodexGeographyModel } from '../../../Journodex/models/journodex-geography.model';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { JournodexRegionsService } from '../../../Journodex/services/journodex-regions.service';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { TimeModel } from '../../../Forms/models/time.model';
import { TimeHelper } from '../../../Forms/helpers/time.helper';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MentionSearchQueryModel } from '../../../../Monitor/Shared/models/mention-search-query.model';
import { FileUploadComponent } from '../../../Forms/components/FileUpload/file-upload.component';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ImageModel } from '../../../Misc/models/image.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { mentionAttributeTypesEnum } from '../../../../Monitor/Shared/enums/mention-attribute-types.enum';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { download } from '../../../Misc/helpers/download/download.helper';
import { MentionInsightTypes } from '../../enums/mention-insight-types.enum';
import { MentionInsightModel } from '../../models/mention-insight.model';
import { AbstractAiDialogComponent } from '../../../Dialogs/components/AbstractAIDialog/Abstract-AI-dialog.component';
import { AIChatCompletionMessageApiModel } from '../../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../../api/ai/enums/AI-chat-completion.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';
import { PartialMentionApiModel } from '../../../../api/mentions/model/mention.model';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { promptLanguageOptions } from '../../../Forms/data/prompt-language-options';
import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { RichTextEditorCkComponent } from '../../../Forms/components/RichTextEditorCK/rich-text-editor-ck.component';
import { HighlightKeywordsPipe } from '../../../Misc/pipes/Highlight/highlight-keywords.pipe';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { IndexHighlightPipe } from '../../../Misc/pipes/Highlight/indexHighlight.pipe';
import { PendoService } from '../../../Misc/services/pendo.service';
import { RichTextEditorConfigModel } from '../../../Forms/components/RichTextEditorCK/model/rich-text-editor-config.model';
import { TranslateMentionsService } from '../../services/translate-mentions.service';
import { MediaApi } from '../../../../api/contentLibrary/models/content-library-media.api.model';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { ContentLibraryMediaService } from '../../../ContentLibrary/services/content-library-media.service';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { ManageMentionOrganizationDialogComponent } from '../ManageMentionOrganization/manage-mention-organization-dialog.component';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaServiceErrorCodesEnum } from '../../../../api/mediaService/mediaResults.api.model';
import { ContentLibrarySearchService } from '../../../ContentLibrary/services/content-library-search.service';
import { UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { UploadOption } from '../../../Forms/models/upload-option.model';
import { CustomInsights } from '@Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService } from '@Monitor/Shared/services/coverage-analysis.service';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { PromptModel } from '@Common/Mentions/models/prompt.model';
import {
    CustomPromptsManagementDialogComponent
} from '../../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';

export enum MentionWorkbenchTabIds {
    EDIT_MENTION = 'EDIT_MENTION',
    INSIGHTS = 'INSIGHTS',
}

enum MentionContentTabIds {
    ORIGINAL,
    TRANSLATED
}

enum MentionWorkbenchTabIndexEnums {
    editMention,
    insights,
}

enum InsightRhpTabIndex {
    mentionDetails,
    insights
}

enum InsightTabIndex {
    systemInsight,
    customInsight
}

@Component({
    selector: 'agpr-mention-workbench-dialog',
    templateUrl: './mention-workbench-dialog.component.html',
    styleUrls: ['./mention-workbench-dialog.component.scss'],
    providers: [],
    standalone: false
})

export class MentionWorkbenchDialogComponent extends AbstractAiDialogComponent implements  OnInit, OnDestroy {
    readonly id = 'mentionWorkbenchDialog';
    readonly mentionWorkbenchTabIds = MentionWorkbenchTabIds;
    readonly mentionContentTabIds = MentionContentTabIds;
    readonly maxWordCount: number = Mention.maxWordCount;
    readonly maxHeadlineCharCount: number = Mention.maxHeadlineCharCount;
    readonly maxAVE: number = Mention.maxAVE;
    readonly maxAudienceReach: number = Mention.maxAudienceReach;
    // B/KB * KB/MB * Number of MB
    readonly MAX_FILE_SIZE = 1024 * 1024 * 2;
    readonly buttonTypes = ButtonTypesEnum;
    readonly mentionInsightTypes = MentionInsightTypes;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly urlFormat: RegExp = /^https?:\/\/[^\/]+\.[^\/]+(\/.*)?/;
    readonly maxEngagement = 999999999;
    readonly systemInsights = [
        MentionInsightTypes.INTELLIGENT_SUMMARY,
        MentionInsightTypes.CONTEXTUAL_ANALYSIS,
        MentionInsightTypes.SENTIMENT_OPINION,
        MentionInsightTypes.MENTION_CHARACTERIZATION,
        MentionInsightTypes.LIKELY_AUDIENCE,
        MentionInsightTypes.BRANDS_MENTIONED,
        MentionInsightTypes.PEOPLE_MENTIONED,
        MentionInsightTypes.PLACES_MENTIONED,
        MentionInsightTypes.KEY_FACTS_AND_FIGURES,
        MentionInsightTypes.QUOTES
    ];
    readonly engagementEventTypeEnum = EngagementEventTypeEnum;
    readonly mediaTypeEnum = MediaTypeEnum;
    readonly mentionAttributeTypesEnum = mentionAttributeTypesEnum;
    readonly acceptTypes = '.txt,.xls,.xlsx,.docx,.csv,.doc,.ppt,.pptx,.rtf,.png,.jpg,.jpeg,.gif,.odp,.ods,.odt,.pdf,.mp3,image/*';
    readonly insightTabIndex = InsightTabIndex;

    get hasUrlImages(): boolean {
        return this.editingMention.images?.some(image => !this.isMediaContent(image.source)) ?? false;
    }

    get hasUploadedImages(): boolean {
        return this.editingMention.images?.some(image => this.isMediaContent(image.source)) ?? false;
    }

    @Output() savedMention: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() insightsChanged: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() refreshSearch: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('fileUpload') fileUpload: FileUploadComponent;
    @ViewChild('insightEditor') insightEditor: RichTextEditorCkComponent;
    @ViewChild('imagePreviewTemplate') imagePreviewTemplate: TemplateRef<any>;
    @ViewChild('imageUpload') imageUpload: ElementRef<HTMLInputElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;


    overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() set mention(mention: Mention) {
        this.originalMention = mention;

        if (mention) {
            this.canGenerateInsight = mention.canGenerateInsight;
            this.isUnsavedMention = !mention.mentionId;
            // editing a mention, so copy the original so we can compare the changes later
            this.editingMention = new Mention(mention.convertToApiMention());
            this.editingMention.contextualKeywords = [];
            mention.contextualKeywords?.forEach(keyword => {
                if (!this.editingMention.contextualKeywords.length
                    || this.editingMention.contextualKeywords.some(item => item.toLocaleLowerCase() !== keyword.toLocaleLowerCase())) {
                    this.editingMention.contextualKeywords.push(keyword);
                }
            });

            if (this.editingMention.language) {
                const normalizedLanguage = this.languageKeyMap[this.editingMention.language] ?? this.editingMention.language;
                this.languageOptions$
                    .pipe(take(1))
                    .subscribe(options => {
                        const selected = options.find(option => option.value === normalizedLanguage);
                        if (selected) {
                            this.languageDropdown = selected;
                        }
                    });
            }


            this.tvEyesUrlConfig.isTVEyes = this.editingMention.isTvEyes();
            if (this.tvEyesUrlConfig.isTVEyes) {
                if (this.editingMention.tvEyesArchiveState === TVEyesArchivedStateEnum.COMPLETED && this.editingMention.viewerUrl) {
                    this.tvEyesUrlConfig.viewerUrl = this.editingMention.viewerUrl;
                } else if (this.editingMention.tvEyesInstaPlayUrl) {
                    this.tvEyesUrlConfig.instaPlayUrl = this.editingMention.tvEyesInstaPlayUrl;
                }
            }
            this.loadAttachments();
        } else {
            // creating a new mention, so nothing to clone from, so create a mention with default settings
            this.editingMention = new Mention();
            this.editingMention.mediaType = new MediaTypeModel(MediaTypeEnum.ONLINE_NEWS);
            this.editingMention.datePublished = new Date();
            this.isUnsavedMention = false;
        }

        this.selectedMediaType = this.mediaTypeOptions?.find(option => option.value === this.editingMention.mediaType.id);
        if (!this.selectedMediaType && this.mediaTypeOptions?.length) {
            // create new mention case, select the first one by default
            this.selectedMediaType = this.mediaTypeOptions[0];
        }
        this.updateMediaType(this.selectedMediaType?.value);

        this.selectSentimentOptionFromValue(this.editingMention.sentiment);
        this.selectedDate = moment(moment.tz(+this.editingMention.datePublished, this.preferences?.timeZone || 'utc').format('YYYY-MM-DD')).toDate();
        this.selectedTime = this.timeHelper.getTimeFromDate(
            new Date(+this.editingMention.datePublished),
            this.preferences ? this.preferences.timeZone : null,
            this.preferences ? this.preferences.timeFormat : timeFormatsEnum.twelveHour
        );
        this.durationTime = this.timeHelper.conventDurationToTime(this.editingMention.duration);

        this.mentionsService.getMentionRegion(this.editingMention).subscribe(region => {
            this.selectedLocation = region;
        });

        // because the translated tab is disabled just before this, it seems to ignore switching tabs automatically, the setTimeout fixes it
        setTimeout(() => {
            if (this.editingMention?.isTranslatedPreferred && this.editingMention.translation) {
                this.selectedMentionContentTab = 1;
            } else {
                this.selectedMentionContentTab = 0;
            }
        }, 0);

        this.checkFormState();
    }

    @Input() set initialTab(initialTab: MentionWorkbenchTabIds) {
        if (initialTab === MentionWorkbenchTabIds.INSIGHTS) {
            this.selectedMentionWorkbenchTab = initialTab;
            this.workbenchTabIndexChange(MentionWorkbenchTabIndexEnums.insights);
        }
    }

    @Input() isNewCustomInsight: boolean = false;
    @Input() customPrompt: PromptModel;
    customPromptLoaded: boolean = false;

    isUnsavedMention: boolean = false;

    internalUser: boolean = false;
    takeOverWidth: number = 80;
    previewImageIndex: number = -1;

    editingMention: Mention = new Mention();
    originalMention: Mention;

    subscriptions: Subscription[] = [];
    preferences: PreferencesModel;
    hideSlideOverlay: boolean = false;
    selectedMentionWorkbenchTab: MentionWorkbenchTabIds = MentionWorkbenchTabIds.EDIT_MENTION;
    selectedMentionWorkbenchTabIndex: MentionWorkbenchTabIndexEnums = MentionWorkbenchTabIndexEnums.editMention;
    selectedMentionContentTab: MentionContentTabIds = MentionContentTabIds.ORIGINAL;
    termsAccepted: boolean = false;

    isSaving: boolean = false;
    changes: boolean = false;

    imageValidatorModel: ImageModel;

    selectedMediaType: DropdownOptionModel<MediaTypeEnum>;
    mediaTypeOptions: DropdownOptionModel<MediaTypeEnum>[];

    selectedLocation: JournodexGeographyModel;
    locationSuggestions: JournodexGeographyModel[];

    languageDropdown: DropdownOptionModel;
    languageOptions$: Observable<DropdownOptionModel[]>;

    selectedDate: Date;
    selectedTime: TimeModel;
    durationTime: TimeModel;

    selectedLanguage: DropdownOptionModel;
    openAiPermission: boolean = false;
    tabIds = MentionContentTabIds;

    aveLabel: string;

    valid: boolean = false;
    showSaveErrors: boolean = false;

    urlError: boolean = false;
    aveError: boolean = false;
    wordCountError: boolean = false;
    shareError: boolean = false;
    quoteError: boolean = false;
    commentError: boolean = false;
    likeError: boolean = false;
    viewError: boolean = false;
    audienceReachError: boolean = false;
    canGenerateInsight: boolean = false;
    promptIsValid: boolean = true;
    allowCustomInsightSave: boolean = false;
    promptGenerateTooltip: string = '';
    maxImages: number = 10;

    adValueUsd: number;
    preferredAdValue: string; // String of AVE value in user preferred currency

    selectedSentiment: number;
    selectedSentimentDropdown: DropdownOptionModel;
    sentimentOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Sentiment.POSITIVE', 1),
        new DropdownOptionModel('Sentiment.NEGATIVE', -1),
        new DropdownOptionModel('Sentiment.NEUTRAL', 0),
        new DropdownOptionModel('Sentiment.NOT_AVAILABLE', null)
    ];

    files: FileUploadModel[] = [];

    contentLibraryImages: MediaSummaryModel[] = [];
    acceptedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    focusedFolder: Folder;
    folderChips: ChipModel[] = [];
    folderList: Folder[] = [];
    folderList$: Observable<Folder[]>;
    folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder).pipe(
                    take(1),
                    switchMap(id => this.folderService.all$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    ))
                ).subscribe(newFolder => {
                    this.selectedFolder(newFolder);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    tvEyesUrlConfig: {
        isTVEyes: boolean;
        instaPlayUrl: string;
        viewerUrl: string;
    } = {
        isTVEyes: false,
        instaPlayUrl: '',
        viewerUrl: ''
    };

    mentionInformation: AttributeModel[] = [];
    bodyParagraphs: string[] = [];
    summaryParagraphs: string[] = [];
    selectedMentionInsightType: MentionInsightTypes;
    selectedPromptLanguageOption: DropdownOptionModel;
    isMentionKeywords: boolean = true;
    mentionKeywordContext: string[] = [];
    manualKeywords: string[] = [];
    highlightKeywords: KeywordHighlightModel[] = [];

    hasMentionInsightsEnabled: boolean = false;
    isInsightDirty: boolean = false;
    contentLibraryMedia: boolean = false;

    promptLanguageOptions = promptLanguageOptions;

    selectedInsightRhpTabIndex = InsightRhpTabIndex.mentionDetails;

    insight: MentionInsightModel = new MentionInsightModel();
    showInsight: boolean = true;
    aiConversations: AIChatCompletionMessageApiModel[] = [];
    isCurrentInsightSaved: boolean = false;
    saveInsightActions = [
        {
            label: 'Mention.insights.saveChange',
            command: () => this.saveInsightChanges()
        },
        {
            label: 'Mention.insights.saveAsNewInsight',
            command: () => this.saveAsNewInsight()
        }
    ];
    insightsHaveChanged: boolean = false;

    // Burrelles no longer counts as a reseller
    isResellerAccount: boolean = false;
    generatedInsight: string = '';

    engagementRepostLabel: string;
    engagementQuoteLabel: string;
    engagementReplyLabel: string;
    engagementLikeLabel: string;
    engagementViewLabel: string;
    languageKeyMap: Record <string, string> = {
        id: 'in'
    };

    ckEditorConfig: RichTextEditorConfigModel;

    uploadOptions = [
        {
            label: this.localizationService.transform('Mention.workbench.image.upload.Url'),
            command: () => {
                this.addImage();
            }
        },
        {
            label: this.localizationService.transform('Mention.workbench.image.upload.newImage'),
            description: 'Mention.editDialog.image.browser.description',
            command: () => {
                this.imageUpload.nativeElement.click();
            }
        },
        {
            label: this.localizationService.transform('Mention.workbench.image.upload.contentLibrary'),
            command: () => {
                this.openContentLibrary();
            }
        }
    ];

    uploadAttachmentOptions: UploadOption[] = [
        {
            label: this.localizationService.transform('Mention.workbench.attachments.uploadNewAttachment'),
            description: 'Mention.editDialog.image.browser.description',
            command: () => {
                this.fileInput.nativeElement.click();
            }
        },
        {
            label: this.localizationService.transform('Mention.workbench.image.upload.contentLibrary'),
            command: () => {
                this.onUploadAttachments();
            }
        }
    ];

    customInsights: CustomInsights[] = [];
    selectedCustomInsight: CustomInsights;
    customInsightCopy: CustomInsights;
    saveCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
    saveAsCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';

    customPromptFocused: boolean;

    selectedInsightTabIndex: number = InsightTabIndex.systemInsight;


    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        private mediaServiceApi: MediaServiceApi,
        public monitorConfigService: MonitorConfigService,
        public mentionsService: MentionsService,
        public regionsDataSource: JournodexRegionsService,
        public timeHelper: TimeHelper,
        public userStateService: CurrentUserStateService,
        private currencyConversionService: CurrencyConversionService,
        private folderService: FolderService,
        private httpClient: HttpClient,
        private configService: ConfigService,
        private messageService: MessageService,
        public mentionSearchService: MentionSearchService,
        private highlightKeywordsPipe: HighlightKeywordsPipe,
        private indexHighlightPipe: IndexHighlightPipe,
        private pendoService: PendoService,
        private translateMentionsService: TranslateMentionsService,
        private contentLibraryMediaService: ContentLibraryMediaService,
        private contentLibrarySearchService: ContentLibrarySearchService,
        private coverageAnalysisService: CoverageAnalysisService
    ) {
        super(coreDialogService, 'MentionWorkbench');
        this.isResellerAccount = configService.hasBrandGUID() && !(configService.brandGUIDIsBurrelles() || configService.brandGUIDIsPaMedia());

        this.languageOptions$ = of(Object.keys(isoLanguageNames)
            .map((key) => {
                const translationKey = this.languageKeyMap[key] ?? key;
                return new DropdownOptionModel(this.localizationService.transform(`Language.${translationKey}`), translationKey);
            })
            .filter((currentOption, index, allOptions) => {
                return index === allOptions.findIndex(option => option.value === currentOption.value);
            })
            .sort((a, b) => a.label.localeCompare(b.label)));

        // initialize the mention to null until otherwise specified
        this.mention = null;

        this.mediaTypeOptions = MediaTypeModel.getAllMediaTypesExceptTikTok().map(item => {
            return {
                value: item.id,
                label: item.name,
                icon: item.icon
            };
        });
        if (!this.selectedMediaType && this.mediaTypeOptions?.length) {
            this.selectedMediaType = this.mediaTypeOptions[0];
        }
        this.folderList$ = this.folderService.all$;
        this.selectedPromptLanguageOption = this.promptLanguageOptions.find(option => option.value === 'en-US');
        if (window.innerWidth < 1400) {
            this.takeOverWidth = 100;
        }
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.insight.name = this.localizationService.transform('Mention.workbench.insight');
        this.showSaveErrors = false;
        this.ckEditorConfig = new RichTextEditorConfigModel({
            enableInsertImage: false,
            enableLineHeight: false,
            enableFontColor: false,
            enableBackgroundColor: false,
            enableHtmlEditor: false,
            enableFontFamily: false,
            enableFontSize: false,
            enableBlockQuote: false,
            enableHeading: false,
            enableAlignment: false,
            enableDirection: false,
            enableIndent: false,
            enableAIRefinement: true,
            enableAIHelper: false,
            enableTranslation: true,
            aiReferenceId: ''
        });
        this.languageOptions$ = this.userStateService.getUILanguageTag().pipe(
            map((tag) =>
                Object.keys(isoLanguageNames)
                    .map(key => {
                        const translationKey = this.languageKeyMap[key] ?? key;
                        return new DropdownOptionModel(this.localizationService.transform(`${tag}.${translationKey}`), translationKey);
                    })
                    .filter((currentOption, index, allOptions) => {
                        return index === allOptions.findIndex(option => option.value === currentOption.value);
                    })
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.subscriptions.push(
            this.userStateService.getPreferences().pipe(filter(x => !!x)).subscribe((preferences) => {
                this.preferences = preferences;
                this.aveLabel = `${this.localizationService.transform('Mention.editDialog.ave.label')} (${UserCurrency.USD})`;

                if ( !!this.editingMention.adValueUsd ) {
                    this.adValueUsd = +this.editingMention.adValueUsd;
                    this.updatePreferredAdValue();
                }

                // this.selectedDate = new Date(+this.editingMention.datePublished);
                this.selectedDate = moment(moment.tz(+this.editingMention.datePublished, this.preferences?.timeZone || 'utc').format('YYYY-MM-DD')).toDate();
                this.selectedTime = this.timeHelper.getTimeFromDate(
                    new Date(+this.editingMention.datePublished),
                    this.preferences ? this.preferences.timeZone : null,
                    this.preferences.timeFormat
                );
            }),
            this.userStateService.getUser().pipe(filter(x => !!x)).subscribe(user => {
                this.internalUser = user.internal;

                // We don't need to ask internal users to accept the terms
                if (this.internalUser) {
                    this.termsAccepted = true;
                }
            }),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(availableMediaTypes => {
                if (availableMediaTypes) {
                    // if the account has access to tiktok, to include it in the create/edit mention modal
                    if (availableMediaTypes.includes(MediaTypeEnum.TIKTOK)) {
                        // we only want to add it if it doesn't exist already
                        if (findIndex(this.mediaTypeOptions, (mediaTypeOption) => {
                            return mediaTypeOption?.id === MediaTypeEnum.TIKTOK;
                        }) === -1) {
                            const tiktokMedia = new MediaTypeModel(MediaTypeEnum.TIKTOK);
                            // Insert in second last place so 'Other' remains as last option
                            this.mediaTypeOptions.splice(-1, 0, {
                                value: tiktokMedia.id,
                                label: tiktokMedia.name,
                                icon: tiktokMedia.icon
                            });

                            this.selectedMediaType = this.mediaTypeOptions?.find(option => {
                                return option.value === this.originalMention?.mediaType?.id;
                            }) || this.mediaTypeOptions[0];

                            this.updateMediaType(this.selectedMediaType?.value);
                        }
                    }
                }
            }),
            this.monitorConfigService.getHasMentionInsightsEnabled().subscribe(x => this.hasMentionInsightsEnabled = x),
            this.folderService.currentResource$.subscribe(focusedFolder => {
                this.focusedFolder = focusedFolder;

                if (focusedFolder?.id && !this.editingMention?.folderIds.find(id => id === this.focusedFolder?.id)) {
                    this.folderChips.push({
                        id: focusedFolder.id,
                        label: focusedFolder.name
                    });

                    this.editingMention.folderIds.push(focusedFolder.id);
                }
            }),
            this.folderService.all$.subscribe((folders) => {
                this.folderList = folders || [];
                this.iniFolderChips();
            }),
            this.userStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),

            this.mentionSearchService.getHighlightKeywords().subscribe(
                highlightKeywords => {
                    this.highlightKeywords = highlightKeywords;
                }
            ),

            this.translateMentionsService.mentionTranslationComplete$.subscribe(id => {
                if (id) {
                    if (this.editingMention?.id === id || this.editingMention?.mentionId === id) {
                        this.checkFormState();
                    }
                }
            }),

            this.contentLibraryMediaService.all$.subscribe(media => {
                this.contentLibraryImages = media;
            }),

            this.coverageAnalysisService.customInsights$.subscribe(prompts => {
                this.customInsights = prompts.filter(item => item.promptType === CustomInsightType.MENTION_INSIGHTS);

                if (this.customPrompt && !this.customPromptLoaded) {
                    this.selectedInsightTabIndex = InsightTabIndex.customInsight;
                    this.selectedCustomInsight = this.customInsights.find(insight => insight.id === this.customPrompt.customInsightId);
                    this.selectCustomPrompt(this.selectedCustomInsight);
                    this.generatedInsight = this.customPrompt.insightText;

                    if (this.generatedInsight) {
                        this.showInsight = true;
                    }

                    this.customPromptLoaded = true;
                }
            })
        );

        if (this.isNewCustomInsight) {
            this.newCustomInsight();
        }
    }

    iniFolderChips(): void {
        this.folderChips = this.editingMention?.folderIds.map(id => {
            return ({
                id: id,
                label: this.folderList.find(x => x.id === id)?.name
            });
        }) || [];
    }

    /**
     * Saves the mention
     * If the mention already exists, perform a patch
     * If the mention is new, then create it
     */
    async save(closeDialog: boolean = true): Promise<void> {
        this.showSaveErrors = true;
        this.validate();

        if (!this.termsAccepted || !this.valid || !this.changes && !this.isSaving) {
            return;
        }

        this.isSaving = true;

        if (this.files.length > 0) {
            this.editingMention.attachments = [];
            const uploadedFiles = await this.fileUpload.uploadFiles();
            uploadedFiles.forEach(file => this.editingMention.attachments.push(file.id));
        }

        this.saveMention(closeDialog);
    }

    private validate(): void {
        let hasImageError = false;
        if (this.editingMention?.images.length) {
            this.editingMention.images.forEach((image, index) => {
                if (!image.source?.trim()) {
                    image.error = true;
                    hasImageError = true;
                }
            });
        }

        if (hasImageError) {
            this.messageService.add({
                severity : messageSeverityEnum.error,
                summary  : this.localizationService.transform('Mention.workbench.image.invalidUrl.summary'),
                detail   : this.localizationService.transform('Mention.workbench.image.invalidUrl.detail')
            });
        }

        this.checkFormState();
    }

    getMediaName(imageUrl: string): string {
        const mediaItem = this.contentLibraryImages.filter(media => media.url === imageUrl);
        return mediaItem.length ? mediaItem[0].name : '';

    }

    isMediaContent(imageUrl: string): boolean {
        return imageUrl && this.contentLibraryImages.some(item => item.url === imageUrl);
    }

    private saveMention(closeDialog: boolean = true): void {
        let request;

        let refetchMention = false;

        if (this.originalMention) {
            // patch changes onto existing mention
            const patch = this.editingMention.getPatch(this.originalMention);

            // If body is changed but wordCount is not updated explicitly, B/E will recalculate wordCount based on new body
            // In this case, we want to refetch the mention to update the wordCount
            if (patch) {
                refetchMention = patch.body && !patch.wordCount;
            }

            // @HACK - we don't want to send this value
            if (patch?.socialData?.engagementLastFetchedDate) {
                patch.socialData.engagementLastFetchedDate = undefined;
            }

            request = this.mentionsService.patchMention(patch);
        } else {
            // create new mentiondynamic
            request = this.mentionsService.createCustomMention(this.editingMention);
        }

        request.pipe(
            switchMap(id => {
                // For create case, it returns the id
                id = id ? id : this.editingMention.mentionId;

                if ((!this.originalMention && id) || refetchMention) {
                    return this.mentionsService.getMentionById(id as string).pipe(tap(mention => {
                        this.canGenerateInsight = mention.canGenerateInsight;
                        this.editingMention = mention;
                    }));
                }

                // update mention scenario
                return of(null);
            }),
            switchMap(() =>
                this.folderService.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                )
            )
        ).subscribe({
            next: () => {
                this.savedMention.emit(this.editingMention);
                this.originalMention = new Mention(this.editingMention.convertToApiMention());
                if (this.focusedFolder) {
                    const folderRemoved = !this.folderChips.find(x => x.id === this.focusedFolder.id);
                    this.refreshSearch.emit(folderRemoved);
                }

                if (closeDialog) {
                    this.closeDialog();
                }

                this.isSaving = false;
                this.changes = false;
            },
            error: () => this.isSaving = false
        });
    }

    workbenchTabChange(tab: TabComponent): void {
        if (this.changes && tab.id === MentionWorkbenchTabIds.INSIGHTS) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'DeactivateGuard.heading',
                    message: 'DeactivateGuard.messageText',
                    showCancel: true,
                    okLabel: 'continue'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.editingMention = new Mention(this.originalMention.convertToApiMention());
                            this.changes = false;
                            this.selectedMentionWorkbenchTab = tab.id as MentionWorkbenchTabIds;
                        } else {
                            this.selectedMentionWorkbenchTab = MentionWorkbenchTabIds.EDIT_MENTION;
                            this.selectedMentionWorkbenchTabIndex = MentionWorkbenchTabIndexEnums.editMention;
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
        } else {
            this.selectedMentionWorkbenchTab = tab.id as MentionWorkbenchTabIds;
        }

        if (tab.id === MentionWorkbenchTabIds.INSIGHTS) {
            this.loadMentionInformation();
            this.bodyParagraphs = (this.editingMention.body || '').split('\n\n');
            this.summaryParagraphs = (this.editingMention.summary || '').split('\n\n');
        }
    }

    workbenchTabIndexChange(index: number): void {
        this.selectedMentionWorkbenchTabIndex = index;
    }

    loadAttachments(): void {
        this.files = [];

        if (this.editingMention.attachments.length > 0) {
            this.mediaServiceApi.getFiles(this.editingMention.attachments).subscribe(mediaResults => {
                mediaResults.mediaResults.forEach(mediaResult => {
                    this.files.push({
                        id: mediaResult.id,
                        name: mediaResult.name,
                        size: mediaResult.size,
                        type: mediaResult.mimeType,
                        url: mediaResult.url
                    });
                });
            });
        }
    }


    openContentLibrary(): void {
        const remainingSlots = 10 - this.editingMention.images.length;
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        if (remainingSlots <= 0) {
            return;
        }

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                selectionMode: true,
                useAcceptedFilesTypes: true,
                multiSelect: true,
                maxSelect: remainingSlots,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles?.length) {
                        if (this.editingMention.images.length + selectedFiles.length > 10) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: this.localizationService.transform('Mention.workbench.editDialog.image.error.summary'),
                                detail: this.localizationService.transform('Mention.workbench.editDialog.image.error.detail')
                            });
                            return;
                        }
                        selectedFiles.forEach(file => {
                            const newImage = new ImageModel();
                            newImage.source = file.url;
                            newImage.name = file.name;
                            this.addImage(newImage);
                        });
                        this.checkFormState();
                    }
                }
            }
        };

        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    /**
     * Checks if there are changes and if the form inputs are valid
     */
    checkFormState(): void {
        this.changes = false;
        if (this.originalMention) {
            const patch = this.editingMention.getPatch(this.originalMention);

            if (patch) {
                this.changes = true;
            } else if (this.files.length > 0) {
                this.changes = true;
            }
        } else {
            // creating a new mention, so there are always changes
            this.changes = true;
        }

        this.valid = true;
        if (!this.editingMention.body) {
            this.valid = false;
        }
        if (!this.editingMention.mediaType) {
            this.valid = false;
        }
        if (!this.editingMention.datePublished) {
            this.valid = false;
        }
        if (this.audienceReachError) {
            this.valid = false;
        }
        if (this.aveError) {
            this.valid = false;
        }
        if (this.wordCountError) {
            this.valid = false;
        }
        if (this.urlError) {
            this.valid = false;
        }
        if (this.editingMention.images.some(x => x.error)) {
            this.valid = false;
        }
        if (this.shareError) {
            this.valid = false;
        }
        if (this.quoteError) {
            this.valid = false;
        }
        if (this.commentError) {
            this.valid = false;
        }
        if (this.likeError) {
            this.valid = false;
        }
        if (this.viewError) {
            this.valid = false;
        }
    }

    /**
     * Sets the media type of the mention when the user changes the media type selection
     */
    updateMediaType(mediaType: MediaTypeEnum): void {
        if (!mediaType) {
            return;
        }

        if (mediaType !== this.editingMention.mediaType.id) {
            this.editingMention.socialAvatarUrl = undefined;
        }

        this.editingMention.mediaType = new MediaTypeModel(mediaType);
        this.checkFormState();

        this.engagementRepostLabel = '';
        this.engagementQuoteLabel = '';
        this.engagementReplyLabel = '';
        this.engagementLikeLabel = '';
        this.engagementViewLabel = '';

        switch(mediaType) {
            case MediaTypeEnum.TWITTER:
                this.engagementRepostLabel = 'Mention.workbench.editDialog.reposts';
                this.engagementQuoteLabel = 'Mention.workbench.editDialog.quotes';
                this.engagementReplyLabel = 'Mention.workbench.editDialog.replies';
                this.engagementLikeLabel = 'Mention.workbench.editDialog.likes';
                this.engagementViewLabel = 'Mention.workbench.editDialog.views';
                break;
            case MediaTypeEnum.INSTAGRAM:
                this.engagementRepostLabel = 'Mention.workbench.editDialog.shares';
                this.engagementReplyLabel = 'Mention.workbench.editDialog.comments';
                this.engagementLikeLabel = 'Mention.workbench.editDialog.likes';
                break;
            case MediaTypeEnum.REDDIT:
                this.engagementRepostLabel = 'Mention.workbench.editDialog.shares';
                this.engagementReplyLabel = 'Mention.workbench.editDialog.comments';
                this.engagementLikeLabel = 'Mention.workbench.editDialog.upvotes';
                this.engagementViewLabel = 'Mention.workbench.editDialog.views';
                break;
            case MediaTypeEnum.LINKEDIN:
                this.engagementReplyLabel = 'Mention.workbench.editDialog.comments';
                this.engagementLikeLabel = 'Mention.workbench.editDialog.likes';
                break;
            case MediaTypeEnum.BLUESKY:
                this.engagementLikeLabel = 'Mention.workbench.editDialog.likes';
                this.engagementQuoteLabel = 'Mention.workbench.editDialog.quotes';
                this.engagementReplyLabel = 'Mention.workbench.editDialog.replies';
                this.engagementRepostLabel = 'Mention.workbench.editDialog.shares';
                break;
            default:
                this.engagementRepostLabel = 'Mention.workbench.editDialog.shares';
                this.engagementReplyLabel = 'Mention.workbench.editDialog.comments';
                this.engagementLikeLabel = 'Mention.workbench.editDialog.likes';
                this.engagementViewLabel = 'Mention.workbench.editDialog.views';
                break;
        }
    }

    /**
     * Sets the author name when the value of the author input changes
     */
    authorChanged(name: string): void {
        const authorName = name?.trim() === '' ? null : name;
        // if the user changes the name, then for now we will remove the ID until we add searching journodex
        this.editingMention.author = {
            id: null,
            name: authorName,
            agilityContactId: null,
            agilityContactURL: null
        };
        this.checkFormState();
    }

    /**
     * Sets the publication name when the value of the outlet input changes
     */
    outletChanged(name: string): void {
        const outletName = name?.trim() === '' ? null : name;
        // if the user changes the name, then for now we will remove the ID until we add searching journodex
        this.editingMention.publication = {
            id: null,
            name: outletName,
            agilityOutletId: null,
            agilityOutletURL: null
        };
        this.checkFormState();
    }

    setToggledImage(index: number) {
        this.previewImageIndex = index;
    }

    toggleImagePreview(index: number): void {
        this.previewImageIndex = this.previewImageIndex === index ? -1 : index;
    }

    /**
     * Updates geographic data for the mention whenever user selects a location
     */
    onSelectLocation() {
        this.editingMention.countyId = this.selectedLocation.county ? this. selectedLocation.county.id : null;
        this.editingMention.cityId = this.selectedLocation.city ? this.selectedLocation.city.id : null;
        this.editingMention.provinceId = this.selectedLocation.province ? this.selectedLocation.province.id : null;
        this.editingMention.countryId = this.selectedLocation.country ? this.selectedLocation.country.id : null;
        this.editingMention.continentId = this.selectedLocation.continent ? this.selectedLocation.continent.id : null;
        this.checkFormState();
    }

    /**
     * Resets geographic data to default when user clears location field
     */
    onClearLocation() {
        this.selectedLocation = new JournodexGeographyModel();
        this.onSelectLocation();
    }

    /**
     * Updates the published date whenever the time or date inputs change
     */
    timeOrDateChanged(): void {
        if (
            !this.selectedDate
                || this.selectedTime.hour === undefined
                || this.selectedTime.hour === null
                || this.selectedTime.minute === undefined
                || this.selectedTime.minute === null
        ) {
            this.editingMention.datePublished = null;
        } else {
            const time = this.selectedTime.changeFormat(timeFormatsEnum.twentyFourHour);
            const momentObject = moment(this.selectedDate);
            momentObject.hours(time.hour);
            momentObject.minutes(time.minute);

            this.editingMention.datePublished = moment.tz(momentObject.format('YYYY-MM-DD HH:mm'), this.selectedTime.timeZone).toDate();
        }

        this.checkFormState();
    }

    /**
     * Validates the URL field when the value changes
     */
    urlChanged(isTVEyes?: boolean): void {
        const value = isTVEyes
            ? this.editingMention.tvEyesInstaPlayUrl
            : this.editingMention.url;

        this.urlError = false;

        if (value && !this.urlFormat.test(value)) {
            this.urlError = true;
        }

        this.checkFormState();
    }

    addImage(image?: ImageModel): void {
        const imageModel = image ?? new ImageModel();

        // The first image is set as preferred by default.
        if (!this.editingMention.images?.length) {
            imageModel.isPreferred = true;
        }

        this.editingMention.images.push(imageModel);
        this.checkFormState();
    }

    removeImage(index: number): void {
        this.editingMention.images.splice(index, 1);
        this.checkFormState();
    }

    setPreferredImage(index: number): void {
        this.editingMention.images.forEach((img, idx) => {
            img.isPreferred = idx === index ? !img.isPreferred : false;
        });
        this.checkFormState();
    }

    imageChanged(image: ImageModel): void {
        const value = image.source;

        if (!value || !this.urlFormat.test(value)) {
            image.error = true;
            this.checkFormState();
        } else {
            this.imageValidatorModel = image;
        }
    }

    imageSourceChanged(): boolean {
        return this.editingMention.images.filter(img => this.isMediaContent(img.source)).length > 0;
    }

    libraryImageSourceChanged(): boolean {
        return this.editingMention.images.filter(img => !this.isMediaContent(img.source)).length > 0;
    }

    imageFailed(image: ImageModel): void {
        image.error = true;
        this.imageValidatorModel = null;
        this.checkFormState();
    }

    imageLoaded(image: ImageModel): void {
        image.error = false;
        this.imageValidatorModel = null;
        this.checkFormState();
    }

    /**
     * Sets the sentiment value of the mention whenever the sentiment input value changes
     */
    sentimentChanged(value: number): void {
        const selectedSentiment = this.sentimentOptions.find(opt => opt.value === value);
        this.editingMention.sentiment = value;
        this.editingMention.sentimentName = this.localizationService.transform(selectedSentiment.label);
        this.checkFormState();
    }

    onLanguageChange(language: string) {
        this.editingMention.language = language;
        this.checkFormState();
    }

    /**
     * Takes the actual value of the sentiment from the mention and maps it to the dropdown options
     */
    selectSentimentOptionFromValue(value: number): void {
        const positive = MentionSearchQueryModel.positiveSentimentRange();
        const neutral = MentionSearchQueryModel.neutralSentimentRange();
        const negative = MentionSearchQueryModel.negativeSentimentRange();

        if (value === null || value === undefined) {
            this.selectedSentiment = null;
        } else if (value <= positive.max && value >= positive.min) {
            this.selectedSentiment = 1;
        } else if (value <= neutral.max && value >= neutral.min) {
            this.selectedSentiment = 0;
        } else if (value <= negative.max && value >= negative.min) {
            this.selectedSentiment = -1;
        }

        const selectedSentimentIndex = this.sentimentOptions.findIndex(option => option.value === this.selectedSentiment);
        if (selectedSentimentIndex > -1) {
            this.selectedSentimentDropdown = this.sentimentOptions[selectedSentimentIndex];
        }
    }

    /**
     * Validates the audience reach (impressions) field when the value changes
     */
    audienceReachChanged(): void {
        const value = this.editingMention.audienceReach;

        this.audienceReachError = false;

        if (value !== undefined && value !== null) {
            if (value > this.maxAudienceReach || value < 0) {
                this.audienceReachError = true;
            }
        }
        this.checkFormState();
    }

    /**
     * Validates the AVE field when the value changes
     */
    aveChanged(): void {
        const value = this.adValueUsd;

        this.aveError = false;

        if (value !== undefined && value !== null) {
            if (value > Mention.maxAVE || value < 0) {
                this.aveError = true;
            }
        }
        if (this.adValueUsd) {
            this.editingMention.adValueUsd = +value;
        } else {
            this.editingMention.adValueUsd = null;
        }

        this.updatePreferredAdValue();
        this.checkFormState();
    }

    updatePreferredAdValue(): void {
        if (this.adValueUsd >= 0) {
            const userCurrency = this.preferences.currency;

            if (userCurrency !== UserCurrency.USD) {
                const adValue = this.currencyConversionService.convertCurrency(this.adValueUsd);
                this.preferredAdValue =  `${adValue} (${userCurrency})`;
            }
        }
    }

    /**
     * Validates the word count field when the value changes
     */
    wordCountChanged(): void {
        const value = this.editingMention.wordCount;

        this.wordCountError = false;

        if (value !== undefined && value !== null) {
            if (value > this.maxWordCount || value < 0) {
                this.wordCountError = true;
            }
        }
        this.checkFormState();
    }

    fileUploadChange(newFileList: FileUploadModel[]): void {
        this.files = newFileList;
        this.editingMention.attachments = this.files.map(file => file.id ? file.id : file.name);
        this.checkFormState();
    }

    selectedFolder(folder: Folder): void {
        let canAdd = true;

        this.folderChips.forEach(x => {
            if (x?.id === folder?.id) {
                canAdd = false;
            }
        });

        if (!canAdd) {
            return;
        }

        this.folderChips.push({
            id: folder.id,
            label: folder.name
        });

        this.folderChips = this.folderChips.slice();

        this.editingMention.folderIds = this.folderChips.map(item => item.id);
        this.checkFormState();
    }

    removeFolder(index: number): void {
        this.folderChips.splice(index, 1);
        this.folderChips = this.folderChips.slice();
        this.editingMention.folderIds = this.folderChips.map(item => item.id);
        this.checkFormState();
    }

    tabChanged(tabIndex: number) {
        this.selectedMentionContentTab = tabIndex;
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;
        if (!files?.length) {
            return;
        }

        const file = files[0];
        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            mergeMap(response => {
                const mediaResult = response.mediaResults?.[0];

                if (mediaResult?.codedError) {
                    this.addUploadErrorMessage(file.name, mediaResult.codedError);
                    return EMPTY;
                }

                return of(mediaResult.media);
            }),
            finalize(() => {
                input.value = '';
            })
        ).subscribe({
            next: (media) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.success.label'),
                    // detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.upload.success.message')
                });
                const newFile = new FileUploadModel();
                newFile.id = media.id;
                newFile.name = media.name;
                newFile.size = media.size;
                newFile.url = media.url;
                newFile.mimeType = media.mimeType;
                this.files.push(newFile);

                this.checkFormState();
                this.contentLibraryMediaService.refreshAll();
                this.contentLibrarySearchService.refreshUsedStorage();
            },
            error: (error) => {
                this.addUploadErrorMessage(file.name, error);
            },
        });
    }

    onUploadAttachments(): void {
        const remainingSlots = 3 - this.files.length;
        if (remainingSlots <= 0) {
            return;
        }
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                selectionMode: true,
                navBarLaunch: false,
                maxSelect: remainingSlots
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles) {
                        if ((this.files?.length || 0) + selectedFiles.length > 3) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: this.localizationService.transform('Mention.workbench.editDialog.attachment.error.summary'),
                                detail: this.localizationService.transform('Mention.workbench.editDialog.attachment.error.detail')
                            });
                            return;
                        }

                        const fileUploads: FileUploadModel[] = selectedFiles.map(file => ({
                            id: file.id,
                            name: file.name,
                            size: file.size,
                            url: file.url,
                            mimeType: file.mimeType,
                        }));

                        const combinedFiles = [...(this.files || []), ...fileUploads].slice(0, 3);
                        this.fileUploadChange(combinedFiles);
                    }
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    languageChanged(language: DropdownOptionModel) {
        this.selectedLanguage = language;
    }

    loadMentionInformation(): void {
        this.mentionInformation = this.mentionsService.getMentionInformation(this.editingMention);
        this.mentionsService.getMentionRegion(this.editingMention)
            .pipe(take(1)).subscribe(region => {

                let regionKeyString = 'na';

                if (region) {
                    regionKeyString = region.displayString;
                }

                const locationAttribute: AttributeModel = {
                    id: mentionAttributeTypesEnum.location,
                    label: 'Mention.details.location',
                    value: regionKeyString,
                    type: attributeTypesEnum.simple,
                    icon: 'fa-solid fa-location-dot'
                };

                this.mentionInformation = [
                    ...this.mentionInformation,
                    locationAttribute
                ];
            });
    }

    downloadAttachment(file: FileUploadModel): void {
        const attachment = {
            id: file.id,
            name: file.name,
            url: file.url,
            mimeType: file.type,
        } as MediaApi;

        let headers = new HttpHeaders;
        headers = headers.set('content-type', attachment.mimeType);
        this.httpClient.get(attachment.url, {'headers': headers, responseType: 'blob'}).subscribe(
            result => {
                download.blob(result, attachment.name);
            }
        );
    }

    selectPrompt(type: MentionInsightTypes): void {
        if (this.canGenerateInsight) {
            this.selectedMentionInsightType = type;
            this.showInsight = false;

            switch (type) {
                case MentionInsightTypes.CONTEXTUAL_ANALYSIS:
                case MentionInsightTypes.SENTIMENT_OPINION:
                    if (this.editingMention.contextualKeywords?.length) {
                        this.mentionKeywordContext = [...this.editingMention.contextualKeywords];
                    } else {
                        this.mentionKeywordContext = [];
                    }

                    const highlightedBody = this.indexHighlightPipe.transform(this.editingMention.body,
                        this.editingMention.contexualKeywordIndices,
                        this.highlightKeywords);
                    const highlightedHeading = this.highlightKeywordsPipe.transform(this.editingMention.headline, this.highlightKeywords);
                    const highlightedTextHtmlDoc = new DOMParser().parseFromString(highlightedBody + highlightedHeading, 'text/html');
                    const containedKeywords = [];

                    this.highlightKeywords?.forEach(item => {
                        if (item.class) {
                            const highlightElements = highlightedTextHtmlDoc.getElementsByClassName(item.class);
                            if (highlightElements) {
                                Array.from(highlightElements).forEach(keywordItem => {
                                    containedKeywords.push(keywordItem.textContent);
                                });
                            }
                        }
                    });

                    containedKeywords?.forEach(keyword => {
                        if (!this.mentionKeywordContext.some(item => item.toLowerCase() === keyword.toLowerCase())
                        ) {
                            this.mentionKeywordContext.push(keyword);
                        }
                    });

                    this.manualKeywords = [];
                    break;
            }

            this.validatePromptFields();
        }
    }

    selectCustomPrompt(item: CustomInsights): void {
        if (this.canGenerateInsight) {
            this.selectedMentionInsightType = MentionInsightTypes.CUSTOM;
            this.selectedCustomInsight = item;
            this.showInsight = false;
            if (this.selectedCustomInsight.id) {
                this.customInsightCopy = this.selectedCustomInsight.clone();
            } else {
                this.customInsightCopy = null;
            }
        }

        this.allowCustomInsightSave = false;
    }

    newCustomInsight(): void {
        const newCustomInsight = new CustomInsights();
        newCustomInsight.promptType = CustomInsightType.MENTION_INSIGHTS;
        this.selectedInsightTabIndex = InsightTabIndex.customInsight;

        this.selectCustomPrompt(newCustomInsight);
    }

    /*
    * Converts and validates the duration field when the value changes
     */
    durationTimeChanged(): void {
        this.editingMention.duration = this.timeHelper.conventTimeToDuration(this.durationTime);
        this.checkFormState();
    }


    newInsight(): void {
        this.insight = new MentionInsightModel();
        this.insight.name = this.localizationService.transform('Mention.workbench.insight');
        this.isCurrentInsightSaved = false;
        this.generatedInsight = '';
        this.showInsight = true;
    }

    clearInsight(): void {
        this.generatedInsight = '';
        this.insight = new MentionInsightModel();
        this.insight.name = this.localizationService.transform('Mention.workbench.insight');
        this.isCurrentInsightSaved = false;
        this.insightEditor?.clearRefineParagraphButton();
    }

    stopInsight(): void {
        this.hasGenerated = true;
        this.abortFetching();
        if (this.generatedInsight?.length < 20) {
            // if the insight length generated is too short, it contains just html tags or some meaningless words
            // We clear it directly.
            this.generatedInsight = '';
        }
    }

    generateInsight(): void {
        this.generatedContent = '';
        this.insightEditor?.clearRefineParagraphButton();
        if (!this.editingMention.body) {
            this.messageService.add(
                {
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Mention.workbench.insights.generation.noBody.errorMessage.summary'),
                    detail: this.localizationService.transform('Mention.workbench.insights.generation.noBody.errorMessage.detail')
                }
            );

            return;
        }

        this.insight = new MentionInsightModel();
        this.isCurrentInsightSaved = false;
        this.insight.name = this.localizationService.transform(`Mention.workbench.insights.system.${this.selectedMentionInsightType}`);

        if (this.editingMention.wordCount <= 100 && this.selectedMentionInsightType === MentionInsightTypes.INTELLIGENT_SUMMARY) {
            // for mention body less than 100 words, we don't call ai to generate insights
            this.generatedInsight = this.localizationService.transform('Mention.workbench.insights.system.bodyTooShort');
            this.showInsight = true;

            return;
        }

        const mediaType = this.editingMention.mediaType.id;
        if ((mediaType === MediaTypeEnum.RADIO
            || mediaType === MediaTypeEnum.TV
            || mediaType === MediaTypeEnum.PODCAST
        ) && this.selectedMentionInsightType === MentionInsightTypes.QUOTES
        ) {
            // For quote insight, since the transcript is a conversation,
            // the AI is recognizing it all as quotes and generally will just reformat the mention transcription in full.
            // Thus, we generate default output "No quotes found." as placeholder
            this.generatedInsight = this.localizationService.transform('Mention.workbench.insights.system.quote.broadcast');
            this.showInsight = true;

            return;
        }

        this.aiConversations = [];
        this.generatedInsight = '';

        const mentionInsightType = this.selectedMentionInsightType === MentionInsightTypes.CUSTOM
            ? MentionInsightTypes.AD_HOC_CUSTOM : this.selectedMentionInsightType;

        const baseMessage: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
            message : JSON.stringify({
                format: mentionInsightType === MentionInsightTypes.AD_HOC_CUSTOM ?
                    `${ChatMessageTypeModel.MENTION_INSIGHTS}_CUSTOM_BASE`
                    : `${ChatMessageTypeModel.MENTION_INSIGHTS}_BASE`,
                languageTag: this.selectedPromptLanguageOption.value,
                mediaType: this.editingMention.mediaType.id
            })
        };

        this.aiConversations.push(baseMessage);

        const insightTypeMessage: any = {
            format: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${mentionInsightType}`,
            languageTag: this.selectedPromptLanguageOption.value,
            mediaType: this.editingMention.mediaType.id
        };

        switch (this.selectedMentionInsightType) {
            case MentionInsightTypes.CONTEXTUAL_ANALYSIS:
                if (this.isMentionKeywords) {
                    insightTypeMessage.keywords = [...this.mentionKeywordContext];
                } else {
                    insightTypeMessage.keywords = [...this.manualKeywords];
                }
                break;
            case MentionInsightTypes.SENTIMENT_OPINION:
                let keywords = [];
                if (this.isMentionKeywords) {
                    keywords = [...this.mentionKeywordContext];
                } else {
                    keywords = [...this.manualKeywords];
                }
                if (keywords?.length) {
                    insightTypeMessage.keywords = keywords;
                    insightTypeMessage.format = `${ChatMessageTypeModel.MENTION_INSIGHTS}_${MentionInsightTypes.SENTIMENT_OPINION_WITH_KEYWORDS}`;
                }
                break;
            case MentionInsightTypes.CUSTOM:
                insightTypeMessage.adHocCustomPrompt = this.selectedCustomInsight.prompt;
                break;
        }

        const insightMessage: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
            message : JSON.stringify(insightTypeMessage)
        };

        this.aiConversations.push(insightMessage);

        if (this.selectedMentionInsightType === MentionInsightTypes.QUOTES) {
            const quotesAttributionMessage: any = {
                format: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${MentionInsightTypes.QUOTES_ATTRIBUTION}`,
                languageTag: this.selectedPromptLanguageOption.value,
                mediaType: this.editingMention.mediaType.id
            };

            const additionalMessage: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
                message : JSON.stringify(quotesAttributionMessage)
            };

            this.aiConversations.push(additionalMessage);
        }

        this.hasGenerated = false;
        this.fetchData(this.configService.getConfig().apiUrl + '/agents/mention-insights', this.selectedMentionInsightType);
        try {
            this.pendoService.trackEvent({
                type: 'INSIGHT_GENERATED',
                data: {
                    insightType: this.selectedMentionInsightType
                }
            });
        } catch (err) {
            // Don't let an error here break things
            console.error(err);
        }
        this.showInsight = true;
    }

    setInsightJson(response: string): any {
        let insightJson: any;

        for (const endFormat of this.endFormatJson) {
            if (!insightJson) {
                try {
                    insightJson = JSON.parse(response + endFormat);
                } catch (e) {
                    // do nothing, format still doesn't make sense from the streaming
                }
            }
        }

        return insightJson;
    }

    extractContent(aiType?: string): void {
        let insight = '';
        switch (aiType) {
            case MentionInsightTypes.QUOTES:
                const quotesArray: any = this.setInsightJson(this.generatedContent)?.filter(quote => {
                    if (!quote?.quote || (quote?.quote === '')) {
                        return false;
                    }
                    if (!quote?.attribution || (quote?.attribution === '')) {
                        return false;
                    }
                    return quote?.attribution?.toLowerCase() !== 'unattributed';
                });

                quotesArray?.forEach((quote) => {
                    insight += `<p>"${quote.quote ? quote.quote : ''}"</p>`;
                    insight += `<p><i>&nbsp;&nbsp;&nbsp;&nbsp;- ${quote.attribution ? quote.attribution : ''}</i></p><br>`;
                });
                this.generatedInsight = insight;
                break;
            case MentionInsightTypes.PEOPLE_MENTIONED:
            case MentionInsightTypes.BRANDS_MENTIONED:
            case MentionInsightTypes.PLACES_MENTIONED:
                insight = '';
                const insightJson: any = this.setInsightJson(this.generatedContent);
                if (insightJson) {
                    Object.keys(insightJson).forEach(key => {
                        if (Array.isArray(insightJson[key]) && insightJson[key].length) {
                            // eslint-disable-next-line max-len
                            insight += `<p style="font-weight: 600; margin: 5px 0"><b>${key.charAt(0).toUpperCase() + key.slice(1)}</b></p><p style="margin-top: 0; margin-bottom: 15px">${insightJson[key].join(', ')}</p><br>`;
                        }
                    });
                }
                this.generatedInsight = insight;
                break;
            case MentionInsightTypes.MENTION_CHARACTERIZATION:
                insight = '';
                const characterizationInsightJson: any = this.setInsightJson(this.generatedContent);
                if (characterizationInsightJson) {
                    Object.keys(characterizationInsightJson).forEach(key => {
                        if (key === 'tone' || key === 'emotions' || key === 'usedterms') {
                            if (Array.isArray(characterizationInsightJson[key])) {
                                if (characterizationInsightJson[key].length) {
                                    // eslint-disable-next-line max-len
                                    insight += `<p style="font-weight: 600; margin: 5px 0"><b>${this.localizationService.transform('Mention.workbench.insights.system.' + key)}</b></p><p style="margin-top: 0; margin-bottom: 15px">${characterizationInsightJson[key].join(', ')}</p><br>`;
                                }
                            } else {
                                if (characterizationInsightJson[key]) {
                                    // eslint-disable-next-line max-len
                                    insight += `<p style="font-weight: 600; margin: 5px 0"><b>${this.localizationService.transform('Mention.workbench.insights.system.' + key)}</b></p><p style="margin-top: 0; margin-bottom: 15px">${characterizationInsightJson[key]}</p><br>`;
                                }
                            }
                        }
                    });
                }
                this.generatedInsight = insight;
                break;
            default:
                this.generatedInsight = this.generatedContent;
                break;
        }
    };

    saveInsight(): void {
        // HACK: <br> looks bad in Outlook, so we remove the ones set above, however this would remove user added new empty lines as well.
        // We will look into this when ck editor is put to use
        this.insight.content = this.generatedInsight.replace(/<br>/g, '');
        const newInsight = new MentionInsightModel();
        newInsight.id = this.insight.id;
        newInsight.name = this.insight.name;
        newInsight.content = this.insight.content;
        if (!this.editingMention.insights?.length) {
            this.editingMention.insights = [];
            newInsight.isPreferred = true;
        }

        this.editingMention.insights.push(newInsight);

        this.persistInsights(newInsight.name, ResourceActionTypes.SAVE);
        this.isCurrentInsightSaved = true;
        this.isInsightDirty = false;
    }

    saveInsightChanges(): void {
        this.editingMention.insights.forEach(insight => {
            if (insight.id === this.insight.id) {
                insight.name = this.insight.name;
                insight.content = this.generatedInsight;
            }
        });

        this.isInsightDirty = false;
        this.persistInsights(this.insight.name, ResourceActionTypes.SAVE);
    }

    saveAsNewInsight(): void {
        const newInsight = new MentionInsightModel(this.insight.toApiModel());
        this.insight = newInsight;
        this.saveInsight();
    }

    preferInsight(insight: MentionInsightModel): void{
        this.editingMention.insights.forEach(item => {
            if (item.id === insight.id) {
                item.isPreferred = true;
            } else {
                item.isPreferred = false;
            }
        });

        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    checkInsightState(): void {
        this.isInsightDirty = !!this.generatedInsight?.trim() && !!this.insight?.name?.trim();

        this.editingMention?.insights?.forEach(insight => {
            if (insight.id === this.insight.id && insight.name === this.insight.name && insight.content === this.generatedInsight) {
                this.isInsightDirty = false;
            }
        });

        if (this.customPrompt && this.selectedCustomInsight && this.selectedCustomInsight.id === this.customPrompt.customInsightId) {
            this.customPrompt.insightText = this.generatedInsight;
        }
    }

    updateInsight(insight: MentionInsightModel): void{
        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    deleteInsight(insight: MentionInsightModel): void {
        this.editingMention.insights = this.editingMention.insights.filter(item => item.id !== insight.id);

        if (this.insight?.id === insight.id) {
            this.isCurrentInsightSaved = false;
        }

        if (this.editingMention.insights.length === 1) {
            this.editingMention.insights[0].isPreferred = true;
        }

        this.persistInsights(insight.name, ResourceActionTypes.DELETE);
    }

    persistInsights(name: string, actionType: ResourceActionTypes): void {
        const patch: PartialMentionApiModel = {
            id: this.editingMention.mentionId,
            insights: this.editingMention.insights?.map(insight => insight.toApiModel())
        };

        this.mentionsService.updateMentionInsights(patch, name, actionType).subscribe(mention => {
            if (mention) {
                this.originalMention.insights = mention.insights;
            }
        });
        this.insightsHaveChanged = true;

        if (this.selectedInsightRhpTabIndex === InsightRhpTabIndex.mentionDetails) {
            this.selectedInsightRhpTabIndex = InsightRhpTabIndex.insights;
        }
    }

    backToPrompts(): void {
        if (this.selectedMentionInsightType === MentionInsightTypes.CUSTOM && this.customInsightCopy) {
            this.selectedCustomInsight.name = this.customInsightCopy.name;
            this.selectedCustomInsight.prompt = this.customInsightCopy.prompt;
        }

        this.selectedMentionInsightType = null;
        this.showInsight = true;
        this.abortFetching();
        this.generatedInsight = '';
        this.insight = new MentionInsightModel();
        this.insight.name = this.localizationService.transform('Mention.workbench.insight');
        this.hasGenerated = true;
        this.isCurrentInsightSaved = false;
        this.insightEditor?.clearRefineParagraphButton();
    }

    handleError(aiType: string, httpStatusCode: number) {
        if (httpStatusCode === 204) {
            // B/E intentionally returns 204 if no content (like no quotes were found) to reply for the mention
            return;
        }

        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Common.dialog.aiHelper.error.description')
            }
        );
        this.hasGenerated = true;
    }

    getRequestModel() {
        return {
            messages: this.aiConversations,
            mentionId: this.editingMention.mentionId,
            mercuryId: this.editingMention.id
        };
    }

    onComplete(aiType?: string){
        switch (aiType) {
            case MentionInsightTypes.BRANDS_MENTIONED:
            case MentionInsightTypes.PEOPLE_MENTIONED:
            case MentionInsightTypes.PLACES_MENTIONED:
            case MentionInsightTypes.QUOTES:
                if (!this.generatedInsight) {
                    this.generatedInsight =
                        this.localizationService.transform(`Mention.workbench.insights.noContentGenerated.${this.selectedMentionInsightType}`);
                }
                break;
            case MentionInsightTypes.MENTION_CHARACTERIZATION:
                let wordCount: string;
                let minutes: string;
                if (this.editingMention.wordCount) {
                    // Calculation based on average reading speed of 250 words per minute
                    wordCount = this.editingMention.wordCount + '';
                    if (this.editingMention.wordCount < 250) {
                        minutes = '<1 ' +  this.localizationService.transform('Mention.workbench.insights.system.minute');
                    } else {
                        const numMinutes = Math.round(this.editingMention.wordCount / 250);

                        if (numMinutes === 1) {
                            minutes = numMinutes + ' ' + this.localizationService.transform('Mention.workbench.insights.system.minute');
                        } else {
                            minutes = numMinutes + ' ' + this.localizationService.transform('Mention.workbench.insights.system.minutes');
                        }
                    }
                } else {
                    wordCount = this.localizationService.transform('Mention.workbench.insights.system.unknown');
                    minutes = this.localizationService.transform('Mention.workbench.insights.system.unknown');
                }

                // eslint-disable-next-line max-len
                this.generatedInsight += `<p style="font-weight: 600; margin: 5px 0"><b>${this.localizationService.transform('Mention.workbench.insights.system.wordCount')}</b></p><p style="margin-top: 0; margin-bottom: 15px">${wordCount}</p><br>`;
                // eslint-disable-next-line max-len
                this.generatedInsight += `<p style="font-weight: 600; margin: 5px 0"><b>${this.localizationService.transform('Mention.workbench.insights.system.timeToRead')}</b></p><p style="margin-top: 0; margin-bottom: 15px">${minutes}</p><br>`;

                break;
            default:
                break;
        }
    }


    close(overlayVisibility: boolean): void {
        if (this.insightsHaveChanged) {
            // Close gets called twice sometimes, marking it false will skip calling it twice
            this.insightsHaveChanged = false;
            this.mentionsService.getMentionById(this.editingMention.mentionId).subscribe(
                mention => {
                    this.insightsChanged.emit(mention);
                    this.abortFetching();
                    this.closeDialog();
                }
            );
        }
        if (!overlayVisibility && !this.insightsHaveChanged) {
            this.abortFetching();
            this.closeDialog();
        }
    }

    validatePromptFields(): void {
        switch (this.selectedMentionInsightType) {
            case MentionInsightTypes.CONTEXTUAL_ANALYSIS:
                if (this.isMentionKeywords) {
                    this.promptIsValid = !!this.mentionKeywordContext?.length;
                } else {
                    this.promptIsValid = !!this.manualKeywords?.length;
                }

                this.promptGenerateTooltip = this.promptIsValid ? '' : 'Mention.workbench.insights.system.promptError.noKeywords';
                break;
            case MentionInsightTypes.CUSTOM:
                this.promptIsValid = !!this.selectedCustomInsight.prompt?.trim();
                this.promptGenerateTooltip = this.promptIsValid ? '' : 'Mention.workbench.insights.prompt.custom.prompt.required';

                break;
            default:
                this.promptIsValid = true;
                this.promptGenerateTooltip = '';
                break;
        }
    }

    engagementChanged(type: EngagementEventTypeEnum): void {
        // engagement views is not included in total engagement
        this.editingMention.engagements = this.editingMention.share + this.editingMention.quote +
            this.editingMention.reply + this.editingMention.like;

        this.editingMention.engagementAge = EngagementAgeEnum.latest;

        switch(type) {
            case EngagementEventTypeEnum.like:
                this.likeError = this.editingMention.like < 0 || this.editingMention.like > this.maxEngagement;
                break;
            case EngagementEventTypeEnum.quote:
                this.quoteError = this.editingMention.quote < 0 || this.editingMention.quote > this.maxEngagement;
                break;
            case EngagementEventTypeEnum.reply:
                this.commentError = this.editingMention.reply < 0 || this.editingMention.reply > this.maxEngagement;
                break;
            case EngagementEventTypeEnum.share:
                this.shareError = this.editingMention.share < 0 || this.editingMention.share > this.maxEngagement;
                break;
            case EngagementEventTypeEnum.view:
                this.viewError = this.editingMention.view < 0 || this.editingMention.view > this.maxEngagement;
                break;
        }
        this.checkFormState();
    }

    openManageMentionOrganizationDialog() {
        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                mention: this.originalMention,
                inputTagIds: this.editingMention.tagIds,
                inputFolderIds: this.editingMention.folderIds,
            },
            outputs: {
                dialogClose: (newMention: Mention) => {
                    if (newMention) {
                        const {tagIds = [], folderIds = []} = newMention;
                        this.originalMention.tagIds = tagIds.slice();
                        this.editingMention.tagIds = tagIds.slice();
                        this.originalMention.folderIds = folderIds.slice();
                        this.editingMention.folderIds = folderIds.slice();
                        this.iniFolderChips();
                        this.checkFormState();
                    }
                }
            }
        });
    }

    onImageSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (!files?.length) {
            return;
        }

        const file = files[0];

        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            mergeMap(response => {
                const mediaResult = response.mediaResults?.[0];

                if (mediaResult?.codedError) {
                    this.addUploadErrorMessage(file.name, mediaResult.codedError);
                    return EMPTY;
                }

                return of(mediaResult.media);
            }),
            finalize(() => {
                input.value = '';
            })
        ).subscribe({
            next: (media) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Mention.workbench.editMention.uploadNewImages.message')
                });

                const newImage = new ImageModel();
                newImage.source = media.url;
                newImage.name = media.name;
                this.addImage(newImage);

                this.contentLibraryMediaService.refreshAll();
                this.contentLibrarySearchService.refreshUsedStorage();
            },
            error: (error) => {
                this.addUploadErrorMessage(file.name, error);
            },
        });
    }

    addUploadErrorMessage(resultName, error) {
        let summaryMessage;
        let detailMessage;
        switch (error.code) {
            case MediaServiceErrorCodesEnum.invalidType:
                summaryMessage = 'Common.mediaService.fileUpload.typeError.heading';
                detailMessage = 'Common.mediaService.fileUpload.typeError.message';
                break;
            case MediaServiceErrorCodesEnum.fileMaxSize:
                summaryMessage = 'Common.mediaService.fileUpload.maxSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.maxSize.message';
                break;
            case MediaServiceErrorCodesEnum.insufficientSpace:
                summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                break;
            case MediaServiceErrorCodesEnum.fileMinSize:
                summaryMessage = 'Common.mediaService.fileUpload.minSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.minSize.message';
                break;
            case MediaServiceErrorCodesEnum.duplicateFile:
                summaryMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.label';
                detailMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.message';
                break;
            case MediaServiceErrorCodesEnum.internalError:
            default:
                if (error.status === 429) {
                    summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                    detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                } else {
                    summaryMessage = 'Common.mediaService.fileUpload.internalError.heading';
                    detailMessage = 'Common.mediaService.fileUpload.internalError.message';
                }
                break;
        }

        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary: this.localizationService.transform(summaryMessage),
            detail: this.localizationService.transform(detailMessage, { name: resultName })
        });
    }

    deleteCustomPrompt(item: CustomInsights): void {
        this.coverageAnalysisService.deleteCustomInsight(item);
    }

    saveCustomInsight(): void {
        if (this.selectedCustomInsight?.id) {
            this.coverageAnalysisService.updateCustomInsight(this.selectedCustomInsight).subscribe(success => {
                if (success) {
                    this.customInsightCopy = this.selectedCustomInsight.clone();
                    this.allowCustomInsightSave = false;
                }
            });
        } else {
            this.doCreateCustomInsight();
        }
    }

    saveAsCustomInsight(): void {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'nameCustomInsight',
                heading: 'Briefings.analysis.customInsight.header',
                okLabel: 'Briefings.analysis.customAnalysis.save',
                placeholder: 'Briefings.analysis.customAnalysisName.placeholder',
                description: 'Briefings.analysis.customInsight.label',
                initialValue: this.selectedCustomInsight.name,
                submit: (name: string) => {
                    this.selectedCustomInsight.name = name;
                    this.doCreateCustomInsight();
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    doCreateCustomInsight() {
        this.coverageAnalysisService.createCustomInsight(this.selectedCustomInsight.clone()).subscribe(customInsightId => {
            if (customInsightId) {
                this.selectedCustomInsight.id = customInsightId;
                this.customInsightCopy = this.selectedCustomInsight.clone();
                this.allowCustomInsightSave = false;
            }
        });
    }

    customInsightFieldChanged(): void {
        this.validatePromptFields();

        if (this.selectedCustomInsight.id) {
            this.allowCustomInsightSave = !this.selectedCustomInsight.equals(this.customInsightCopy);
        } else {
            this.allowCustomInsightSave = true;
        }

        if (!this.selectedCustomInsight.name?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';

            return;
        }

        if (!this.selectedCustomInsight.prompt?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';

            return;
        }

        this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
        this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';
    }

    openCustoPromptDialog() {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: document.getElementById('main-content'),
                selectedPromptType: CustomInsightType.MENTION_INSIGHTS
            },
            outputs: {
               dialogClose: () => {
                    const selectCustomInsightId = this.selectedCustomInsight?.id;
                    if (selectCustomInsightId) {
                        const updatedInsight = this.customInsights.find((item) => item.id === selectCustomInsightId);
                        if (updatedInsight) {
                            this.stopInsight();
                            this.selectCustomPrompt(updatedInsight);
                        } else {
                            this.backToPrompts();
                        }
                    }
                }
            }
        };
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
